import * as React from 'react';

// @ts-expect-error
import {Bar, Line} from 'recharts';
import _ from 'underscore';

import {groupOtherSegments, QUOTA_REPORT_KEY} from 'nutshell-core/json-to-report-data';
import type {ChartBucket, TimeseriesReportData, ComparisonRangeType} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
import * as ReportColors from 'shells/colors/reports';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
// @ts-expect-error
import {getInstanceCurrencyPrefix} from 'shells/utils/currency';

import {
    FORECAST,
    SALES,
    LOSSES,
    SEGMENT_PRIORITY_FACET,
    SEGMENTS,
    ACTIVITY,
    NONE_BUCKET_NAME,
    isMarketingReportKey,
    getReportSegmentDisplayType,
    ENGAGEMENT_LEADS,
    ENGAGEMENT_SALES,
    WIDGET_OPENS,
} from '../report-constants';
import type {
    ReportKey,
    ReportFacet,
    ChartReportParams,
    ReportInfo,
    ReportSegment,
} from '../report-types';

import {getSegmentColor} from './get-segment-color';
import {TimeseriesChart} from './hero/timeseries-chart';

import styles from './segmented-timeseries-chart.module.css';

const MAX_SEGMENTS = 6;

const SALES_COLOR = ReportColors.WON_GREEN;
const LOSSES_COLOR = ReportColors.LOST_RED;
const PIPELINE_COLOR = ReportColors.BASE_BAR_COLOR;

const REFERENCE_OPACITY = 0.3;
const IS_ANIMATION_ACTIVE = false;

/**
 * Resets all legend items to default styling
 *
 * @return {void}
 */
function disableActiveLegendStyles() {
    // Disable "active" styles from all legend items
    // NOTE: can't use `forEach` because IE11 does not support it.  :(
    const legendItemNodes = document.querySelectorAll('.recharts-legend-item');
    if (legendItemNodes) {
        for (let i = 0; i < legendItemNodes.length; ++i) {
            // @ts-expect-error
            legendItemNodes[i].style.removeProperty('text-decoration');
            // @ts-expect-error
            legendItemNodes[i].style.removeProperty('color');
        }
    }
}

/**
 * Sets "active" styles on the clicked legend item.
 *
 * @param {string[]} legendItems              A sorted list of the legend item names
 * @param {string} clickedLegendItem          The name of the legend item which was clicked
 * @param {string} [previousActiveLegendItem] The name of the legend item which was already set as active before the click
 * @return {void}
 */
function setActiveLegendItemStyle(
    legendItems: Array<string>,
    clickedLegendItem: string,
    previousActiveLegendItem?: string | null
) {
    const clickedLegendItemIndex = legendItems.indexOf(clickedLegendItem);
    const clickedLegendItemClassName = `.recharts-legend-item.legend-item-${clickedLegendItemIndex}`;
    const clickedLegendItemNode = document.querySelector(clickedLegendItemClassName);
    if (clickedLegendItemNode) {
        if (previousActiveLegendItem !== clickedLegendItem) {
            // @ts-expect-error
            clickedLegendItemNode.style.textDecoration = 'underline';
            // @ts-expect-error
            clickedLegendItemNode.style.color = colors.greyDk;
        }
    }
}

type SegmentedTimeseriesChartProps = {
    reportKey: ReportKey;
    reportParams: ChartReportParams;
    reportInfo?: ReportInfo;
    currencyPrefix: string;
    chartData: TimeseriesReportData;
    xAxisLabel?: React.ReactNode;
    isRefreshing?: boolean;
    onClickChartSegment?: (...args: any) => any;
};

type SegmentedTimeseriesChartState = {
    hoveredItem: string | null | undefined;
    selectedLegendItem: string | null | undefined;
    chartBuckets: Array<ChartBucket>;
    sortedSegmentNames?: Array<string>;
};

export class SegmentedTimeseriesChart extends React.Component<
    SegmentedTimeseriesChartProps,
    SegmentedTimeseriesChartState
> {
    constructor(props: SegmentedTimeseriesChartProps) {
        super(props);
        const {buckets: chartBuckets, sortedSegmentNames} = groupOtherSegments(
            props.chartData,
            MAX_SEGMENTS,
            SEGMENT_PRIORITY_FACET
        );
        this.state = {
            hoveredItem: null,
            selectedLegendItem: null,
            chartBuckets,
            sortedSegmentNames,
        };
    }

    static defaultProps = {
        currencyPrefix: getInstanceCurrencyPrefix(),
    };

    UNSAFE_componentWillReceiveProps(nextProps: SegmentedTimeseriesChartProps) {
        // Update chartBuckets and sortedSegmentNames if the data has changed
        if (nextProps.chartData !== this.props.chartData) {
            const {buckets: chartBuckets, sortedSegmentNames} = groupOtherSegments(
                nextProps.chartData,
                MAX_SEGMENTS,
                SEGMENT_PRIORITY_FACET
            );
            this.setState(() => ({chartBuckets, sortedSegmentNames}));
            // If the data changed in such a way as to have different segment names, reset legend item active states
            if (!_.isEqual(sortedSegmentNames, this.state.sortedSegmentNames)) {
                disableActiveLegendStyles();
                if (
                    sortedSegmentNames &&
                    this.state.selectedLegendItem &&
                    sortedSegmentNames.includes(this.state.selectedLegendItem)
                ) {
                    // If the previously-selected legend item is still in the data, re-apply active styling
                    setActiveLegendItemStyle(sortedSegmentNames, this.state.selectedLegendItem);
                } else {
                    // Otherwise, unselect it
                    this.setState(() => ({selectedLegendItem: null}));
                }
            }
        }
    }

    render() {
        const {reportInfo, chartData, xAxisLabel, reportKey, ...restProps} = this.props;

        const renderChartComponents = renderSeries(
            reportKey,
            reportInfo ||
                (chartData && {
                    reports: chartData.reports,
                    referenceReports: chartData.referenceReports,
                }), // Fall back to reportMap in chartData if reportInfo not provided (e.g. from SSR)
            this.props.reportParams.segment,
            this.props.reportParams.useConfidence === 'true',
            this.props.reportParams.compareTo,
            this.state.sortedSegmentNames,
            this.state.hoveredItem || this.state.selectedLegendItem
        );

        return (
            <div>
                <TimeseriesChart
                    {...restProps}
                    // @ts-expect-error
                    reportKey={reportKey}
                    legendAlignment={isMarketingReportKey(reportKey) ? 'left' : 'right'}
                    xAxisLabel={xAxisLabel}
                    chartBuckets={this.state.chartBuckets}
                    renderSeries={renderChartComponents}
                    onMouseEnterLegendItem={this.handleMouseEnterLegendItem}
                    onMouseLeaveLegendItem={this.handleMouseLeaveLegendItem}
                    onLegendItemClick={this.handleLegendItemClick}
                />
                {this.props.isRefreshing && <LoadingIcon className={styles['refreshing-icon']} />}
            </div>
        );
    }

    handleMouseEnterLegendItem = (legendItem: {value: string}) => {
        this.setState({
            hoveredItem: legendItem.value,
        });
    };

    handleMouseLeaveLegendItem = () => {
        this.setState({
            hoveredItem: null,
        });
    };

    handleLegendItemClick = (chartItem: {value: string}) => {
        if (!this.state.sortedSegmentNames) return;
        const sortedSegmentNames = this.state.sortedSegmentNames;
        disableActiveLegendStyles();
        setActiveLegendItemStyle(
            sortedSegmentNames,
            chartItem.value,
            this.state.selectedLegendItem
        );

        // Toggle or set the selectedLegendItem state
        this.setState((state) => ({
            selectedLegendItem:
                state.selectedLegendItem === chartItem.value ? null : chartItem.value,
        }));
    };
}

/**
 * Get the total count or value summary for a given segment
 *
 * @param {Object} data          The chart bucket to look in
 * @param {string} name          Name of the segment you want
 * @param {string} facet         count or total
 * @param {boolean} useWeighted  determines whether to use summary or summaryWeighted
 * @param {boolean} isReference  if true, get summary from referenceBucketData, otherwise from bucketData
 * @return {number}              The value to plot
 */
function getSegmentSummary(
    data: ChartBucket,
    // @ts-expect-error
    name,
    // @ts-expect-error
    facet,
    // @ts-expect-error
    useWeighted,
    isReference?: boolean
): number | null | undefined {
    let summary;
    if (isReference) {
        if (!data.referenceBucketData) return null;
        summary = useWeighted
            ? data.referenceBucketData.summaryWeighted
            : data.referenceBucketData.summary;
    } else {
        if (!data.bucketData) return null;
        summary = useWeighted ? data.bucketData.summaryWeighted : data.bucketData.summary;
    }
    if (!summary) return null;

    // If the segment isn't found, return null.  This can happen if there are different segments in
    // reference data than in the baseline data.
    if (!summary.segments[name]) return null;

    // @ts-expect-error
    return summary.segments[name][facet];
}

function renderSeries(
    reportKey: ReportKey,
    reportInfo?: ReportInfo,
    segment?: ReportSegment,
    useConfidence?: boolean,
    compareTo?: ComparisonRangeType,
    seriesNames?: Array<string>,
    filteredSeriesName?: string | null
) {
    return function ({facet}: {facet: ReportFacet}) {
        let chartComponents = [];

        if (segment && seriesNames) {
            // When calculating colors, we don't want to include "None" as a segment,
            // as it gets its own special color.
            const segmentCount = seriesNames.filter((name) => name !== NONE_BUCKET_NAME).length;
            let hasNoneSegment = false;
            chartComponents = seriesNames.map((name, index) => {
                // If we removed the `none` bucket from the count, we need to adjust the index too
                if (name === NONE_BUCKET_NAME) {
                    hasNoneSegment = true;
                }

                const filteredIndex = hasNoneSegment ? index - 1 : index;

                const color = getSegmentColor(
                    segment,
                    name,
                    segmentCount,
                    filteredIndex,
                    reportKey
                );

                const getItemData = (data: ChartBucket) => {
                    // If we want to only show one series, we can remove the dataKey
                    // for all the other series, which keeps their legend entries
                    // and maintains the correct colors (which is screwed up if you
                    // try to only render the desired bar).
                    if (filteredSeriesName && filteredSeriesName !== name) return 0;
                    if (!data.bucketData) return null;

                    return getSegmentSummary(data, name, facet, useConfidence);
                };

                const segmentInfo = SEGMENTS.find((seg) => seg.id === segment);
                const stackId = segmentInfo && segmentInfo.shouldStack[facet] ? 1 : index * 2 + 1;

                const segmentComponentType = getReportSegmentDisplayType(reportKey);
                const segmentComponents =
                    segmentComponentType === 'line'
                        ? [
                              <Line
                                  key={name}
                                  dot={false}
                                  isAnimationActive={IS_ANIMATION_ACTIVE}
                                  dataKey={getItemData}
                                  name={name}
                                  stackId={stackId}
                                  curve={true}
                                  legendType='square'
                                  fillOpacity={1}
                                  fill={color}
                                  stroke={color}
                              />,
                          ]
                        : [
                              <Bar
                                  key={name}
                                  isAnimationActive={IS_ANIMATION_ACTIVE}
                                  dataKey={getItemData}
                                  name={name}
                                  stackId={stackId}
                                  curve={true}
                                  legendType='square'
                                  fillOpacity={1}
                                  fill={color}
                                  stroke={color}
                              />,
                          ];

                if (compareTo) {
                    const getReferenceItemData = (data: ChartBucket) => {
                        if (filteredSeriesName && filteredSeriesName !== name) return 0;
                        if (!data.referenceBucketData) return null;

                        return getSegmentSummary(data, name, facet, useConfidence, true);
                    };
                    const referenceStackId =
                        segmentInfo && segmentInfo.shouldStack[facet] ? 0 : index * 2;

                    segmentComponents.push(
                        <Bar
                            key={`reference_${name}`}
                            isAnimationActive={IS_ANIMATION_ACTIVE}
                            dataKey={getReferenceItemData}
                            name={`reference_${name}`}
                            stackId={referenceStackId}
                            curve={true}
                            legendType='none'
                            fillOpacity={REFERENCE_OPACITY}
                            fill={color}
                        />
                    );
                }

                return segmentComponents;
            });
        } else {
            // Using reports not segments
            if (!reportInfo || !reportInfo.reports || !reportInfo.reports.length) return undefined;
            const defaultReport = {id: '', name: 'Leads'} as const;
            const mainReport = reportInfo.reports[0];

            if (reportKey === FORECAST) {
                const pipelineKey = useConfidence ? 'pipeline_weighted' : 'pipeline';
                const pipelineReport =
                    reportInfo.reports.find((r) => r.key === pipelineKey) || defaultReport;
                const getPipelineData = (data: ChartBucket) => {
                    if (filteredSeriesName && filteredSeriesName !== pipelineReport.name) return 0;
                    if (!data.bucketData) return null;

                    // @ts-expect-error
                    return data.bucketData.reportData[pipelineReport.id][facet];
                };

                const getSalesData = (data: ChartBucket) => {
                    // @ts-expect-error
                    if (filteredSeriesName && filteredSeriesName !== mainReport.name) return 0;
                    if (!data.bucketData) return null;

                    // @ts-expect-error
                    return data.bucketData.reportData[mainReport.id][facet];
                };

                // NOTE: If you want to change the name being supplied to these Bar components,
                // you need to change the `reports` map that is being provided to SegmentedTimeseriesChart.
                // This is becuase the name needs to match the map that is provided to the tooltips as well.
                chartComponents = [
                    <Bar
                        key={pipelineReport.name}
                        isAnimationActive={IS_ANIMATION_ACTIVE}
                        dataKey={getPipelineData}
                        name={pipelineReport.name}
                        stackId='1'
                        fill={PIPELINE_COLOR}
                        legendType='square'
                    />,
                    <Bar
                        // @ts-expect-error
                        key={mainReport.name}
                        isAnimationActive={IS_ANIMATION_ACTIVE}
                        dataKey={getSalesData}
                        // @ts-expect-error
                        name={mainReport.name}
                        stackId='1'
                        fill={SALES_COLOR}
                        legendType='square'
                    />,
                ];

                if (compareTo && reportInfo.referenceReports) {
                    const mainReferenceReport = reportInfo.referenceReports[0];
                    const pipelineReferenceKey = useConfidence ? 'pipeline_weighted' : 'pipeline';
                    const PipelineReferenceReport =
                        reportInfo.referenceReports.find((r) => r.key === pipelineReferenceKey) ||
                        defaultReport;

                    const getReferencePipelineData = (data: ChartBucket) => {
                        if (
                            filteredSeriesName &&
                            filteredSeriesName !== PipelineReferenceReport.name
                        )
                            return 0;
                        if (!data.referenceBucketData) return null;

                        // @ts-expect-error
                        return data.referenceBucketData.reportData[PipelineReferenceReport.id][
                            facet
                        ];
                    };

                    const getReferenceSalesData = (data: ChartBucket) => {
                        // @ts-expect-error
                        if (filteredSeriesName && filteredSeriesName !== mainReferenceReport.name)
                            return 0;
                        if (!data.referenceBucketData) return null;

                        // @ts-expect-error
                        return data.referenceBucketData.reportData[mainReferenceReport.id][facet];
                    };

                    // @ts-expect-error
                    chartComponents.push([
                        <Bar
                            key={`reference_${PipelineReferenceReport.name}`}
                            isAnimationActive={IS_ANIMATION_ACTIVE}
                            dataKey={getReferencePipelineData}
                            name={`reference_${PipelineReferenceReport.name}`}
                            stackId='0'
                            fill={PIPELINE_COLOR}
                            fillOpacity={REFERENCE_OPACITY}
                            legendType='none'
                        />,
                        <Bar
                            // @ts-expect-error
                            key={`reference_${mainReferenceReport.name}`}
                            isAnimationActive={IS_ANIMATION_ACTIVE}
                            dataKey={getReferenceSalesData}
                            // @ts-expect-error
                            name={`reference_${mainReferenceReport.name}`}
                            stackId='0'
                            fill={SALES_COLOR}
                            fillOpacity={REFERENCE_OPACITY}
                            legendType='none'
                        />,
                    ]);
                }
            } else {
                const getData = (data: ChartBucket) => {
                    // @ts-expect-error
                    if (filteredSeriesName && filteredSeriesName !== mainReport.name) return 0;
                    if (!data.bucketData) return null;

                    // @ts-expect-error
                    return data.bucketData.reportData[mainReport.id][facet];
                };
                let barColor = ReportColors.BASE_BAR_COLOR;
                if (reportKey === SALES) {
                    barColor = SALES_COLOR;
                } else if (reportKey === LOSSES) {
                    barColor = LOSSES_COLOR;
                } else if (reportKey === ENGAGEMENT_LEADS || reportKey === ENGAGEMENT_SALES) {
                    barColor = colors.greenDk;
                } else if (reportKey === WIDGET_OPENS) {
                    barColor = colors.blueDk;
                }

                chartComponents = [
                    <Bar
                        // @ts-expect-error
                        key={mainReport.name}
                        isAnimationActive={IS_ANIMATION_ACTIVE}
                        dataKey={getData}
                        // @ts-expect-error
                        name={mainReport.name}
                        stackId='1'
                        fill={barColor}
                        legendType='square'
                    />,
                ];

                if (compareTo && reportInfo.referenceReports) {
                    const mainReferenceReport = reportInfo.referenceReports[0];
                    const getReferenceData = (data: ChartBucket) => {
                        // @ts-expect-error
                        if (filteredSeriesName && filteredSeriesName !== mainReferenceReport.name)
                            return 0;
                        if (!data.referenceBucketData) return 0;

                        // @ts-expect-error
                        return data.referenceBucketData.reportData[mainReferenceReport.id][facet];
                    };

                    chartComponents.push(
                        <Bar
                            // @ts-expect-error
                            key={`reference_${mainReferenceReport.name}`}
                            isAnimationActive={IS_ANIMATION_ACTIVE}
                            dataKey={getReferenceData}
                            // @ts-expect-error
                            name={`reference_${mainReferenceReport.name}`}
                            stackId='0'
                            fillOpacity={REFERENCE_OPACITY}
                            fill={barColor}
                            stroke={barColor}
                            legendType='none'
                        />
                    );
                }
            }
        }

        // Add quota line if faceted by lead values
        if (shouldIncludeQuota(reportKey, facet, reportInfo)) {
            const quotaLine = getQuotaLine(reportInfo, filteredSeriesName, facet);
            // @ts-expect-error
            if (quotaLine) chartComponents.push(quotaLine);
        }

        return chartComponents;
    };
}

function shouldIncludeQuota(
    reportKey: ReportKey,
    facet: ReportFacet,
    reportInfo?: ReportInfo | null
) {
    if (!NutClientConfig.hasQuotas) {
        return false;
    }

    // Must have report info in order to determine which is the quota
    if (!reportInfo) return false;

    // Engagement sales report should not show quota line
    if (reportKey === ENGAGEMENT_SALES) {
        return false;
    }

    // If it's an activity report, try to include a quota line.  The server might
    // not have sent a `quota` report, but if not we'll detect that later and short-circuit
    if (reportKey === ACTIVITY) {
        return true;
        // Lead reports must be faceted by `total`
    } else if (facet === 'total') {
        return true;
    }

    // Default to excluding, just to be safe;
    return false;
}

function getQuotaLine(
    reportInfo: ReportInfo | null | undefined,
    filteredSeriesName: string | null | undefined,
    facet: ReportFacet
) {
    if (!reportInfo) return undefined;

    const quotaReport = reportInfo.reports.find((r) => r.key === QUOTA_REPORT_KEY);
    // If we can't find a quota report, don't try to render a quota line
    if (typeof quotaReport === 'undefined') {
        return undefined;
    }

    const getQuotaData = (data: ChartBucket) => {
        // If we want to only show one series, we can remove the dataKey
        // for all the other series, which keeps their legend entries
        // and maintains the correct colors (which is screwed up if you
        // try to only render the desired bar).
        if (filteredSeriesName && filteredSeriesName !== quotaReport.name) return 0;
        if (!data.bucketData) return null;

        // @ts-expect-error
        return data.bucketData.reportData[quotaReport.id][facet];
    };

    return (
        <Line
            key='Quota'
            isAnimationActive={IS_ANIMATION_ACTIVE}
            type='monotone'
            width={5}
            dataKey={getQuotaData}
            name='Quota'
            stroke={colors.greyDk}
            strokeDasharray='5 5'
            strokeWidth={2}
            dot={{strokeDasharray: 0}}
        />
    );
}
